/**
 * Tier Reward Manager
 * 
 * Features:
 * - Manages tier-based gift products in cart based on total thresholds
 * - Automatically adds/removes tier gifts when thresholds are met/unmet
 * - Monitors and removes orphaned gift items (gifts with _gift property not belonging to any active tier)
 * - Periodic cleanup runs every 30 seconds to remove stale gifts from old tier configurations
 */
class TierRewardManager extends HTMLElement {
  static isCartListenerInitialized = false;
  static allInstances = new Set();
  static isGloballyInitialized = false;
  static sharedCart = null;
  static lastGlobalCartUpdate = 0;
  static isProcessingGlobally = false;
  static operationLock = false;
  static operationQueue = [];

  constructor() {
    super();
    this.config = JSON.parse(this.querySelector("#tier-config").textContent);
    this.tiers = this.config.tiers.sort((a, b) => a.threshold - b.threshold);
    this.currencyFactor = 1;
    this.cart = null;
    this.cartEl =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.mainCartItems = document.querySelector("cart-items");
    this.isCartPage = window.location.pathname === "/cart";
    this.sections = null;
    this.progressBar = this.querySelector(".tier-sale-progress-bar");
    this.isProcessingCart = false;
    this.initialLoad = true; 
    this.lastCartUpdate = 0;
    this.isUIOnly = document.querySelector('tier-progress.tier-sale-banner') ? this.hasAttribute('data-ui-only') : false;
    this.orphanedGiftInterval = null;
    
    TierRewardManager.allInstances.add(this);
  }

  connectedCallback() {
    if (TierRewardManager.sharedCart) {
      this.cart = TierRewardManager.sharedCart;
      this.lastCartUpdate = TierRewardManager.lastGlobalCartUpdate;
    }

    // Only do full init once globally
    if (!TierRewardManager.isGloballyInitialized) {
      TierRewardManager.isGloballyInitialized = true;
      this.init();
    } else {
      this.listenToCartChanges();
      this.updateUI();
      
      if (!this.isUIOnly) {
        this.performInitialOrphanedGiftCheck();
      }
      
      setTimeout(() => {
        this.initialLoad = false;
      }, 100);
    }
  }

  disconnectedCallback() {
    // Clean up when element is removed from DOM
    TierRewardManager.allInstances.delete(this);
    
    // Clear the orphaned gift monitor interval
    if (this.orphanedGiftInterval) {
      clearInterval(this.orphanedGiftInterval);
      this.orphanedGiftInterval = null;
    }
  }

  /**
   * Acquire operation lock - returns true if lock acquired, false if already locked
   */
  static async acquireLock(timeout = 3000) {
    const startTime = Date.now();
    
    while (TierRewardManager.operationLock) {
      if (Date.now() - startTime > timeout) {
        console.warn("TierRewardManager: Lock acquisition timeout");
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    TierRewardManager.operationLock = true;
    return true;
  }

  /**
   * Release operation lock
   */
  static releaseLock() {
    TierRewardManager.operationLock = false;
  }

  static disableCheckoutButton() {
    const checkoutButtons = document.querySelectorAll('.cart__checkout-button, [name="checkout"]');
    checkoutButtons.forEach(button => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
    });
  }

  static enableCheckoutButton() {
    const checkoutButtons = document.querySelectorAll('.cart__checkout-button, [name="checkout"]');
    checkoutButtons.forEach(button => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.style.opacity = '';
      button.style.cursor = '';
    });
  }

  static setupCheckoutButtonObserver() {
    // Only set up once
    if (window._tierCheckoutObserverInstalled) {
      return;
    }
    window._tierCheckoutObserverInstalled = true;

    const observer = new MutationObserver((mutations) => {
      if (TierRewardManager.isProcessingGlobally) {
        TierRewardManager.disableCheckoutButton();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    window._tierCheckoutObserver = observer;
  }

  static setupFetchInterceptor() {
    if (window._tierFetchInterceptorInstalled) {
      return;
    }
    window._tierFetchInterceptorInstalled = true;

    const originalFetch = window.fetch;
    
    window.fetch = async function(...args) {
      const [url, options] = args;
      const method = options?.method?.toUpperCase() || 'GET';
      
      const isCartModification = url && method !== 'GET' && (
        url.includes('/cart/change.js') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update.js') ||
        url.includes('/cart/update') ||
        url.includes('/cart/add.js') ||
        url.includes('/cart/add')
      );

      if (isCartModification) {
        TierRewardManager.disableCheckoutButton();
        TierRewardManager.isProcessingGlobally = true;
      }

      try {
        const response = await originalFetch.apply(this, args);
        
        if (isCartModification) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "fetch-interceptor" });
        }

        return response;
      } catch (error) {
        if (isCartModification) {
          TierRewardManager.isProcessingGlobally = false;
          TierRewardManager.enableCheckoutButton();
        }
        throw error;
      }
    };
  }

  async init() {
    TierRewardManager.setupFetchInterceptor();
    TierRewardManager.setupCheckoutButtonObserver();
    
    await this.getCart();
    if (!this.isUIOnly) {
      await this.performInitialOrphanedGiftCheck();
      
      await this.syncTiersWithCart();
      
      this.startOrphanedGiftMonitor();
      
      this.setupCheckoutValidation();
    }
    this.listenToCartChanges();
    this.updateUI();
    setTimeout(() => {
      this.initialLoad = false;
    }, 500);
  }

  setupCheckoutValidation() {
    if (window._tierCheckoutValidationInstalled) {
      return;
    }
    window._tierCheckoutValidationInstalled = true;

    document.addEventListener('click', async (event) => {
      const checkoutButton = event.target.closest('.cart__checkout-button, [name="checkout"]');
      
      if (!checkoutButton) {
        return;
      }

      if (TierRewardManager.isProcessingGlobally) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const instance = Array.from(TierRewardManager.allInstances).find(inst => !inst.isUIOnly);
      
      if (!instance) {
        return;
      }

      await instance.getCart();
      
      const orphanedItems = instance.getOrphanedGiftItems();
      const { itemsToRemove, itemsToUpdate } = instance.getInvalidQuantityGiftItems();
      const unqualifiedItems = instance.getUnqualifiedGiftItems();
      
      const hasIssues = orphanedItems.length > 0 || 
                        itemsToRemove.length > 0 || 
                        itemsToUpdate.length > 0 || 
                        unqualifiedItems.length > 0;
      
      if (!hasIssues) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      TierRewardManager.isProcessingGlobally = true;
      TierRewardManager.disableCheckoutButton();

      try {
        await instance.fixInvalidGiftItems();
        
        instance.rerenderCartContents();
        
        setTimeout(() => {
          TierRewardManager.isProcessingGlobally = false;
          TierRewardManager.enableCheckoutButton();
          checkoutButton.click();
        }, 300);
        
      } catch (error) {
        console.error("Error fixing gifts before checkout:", error);
        TierRewardManager.isProcessingGlobally = false;
        TierRewardManager.enableCheckoutButton();
        
        checkoutButton.click();
      }
    }, true);
  }

  async performInitialOrphanedGiftCheck() {
    try {
      const orphanedItems = this.getOrphanedGiftItems();
      const { itemsToRemove, itemsToUpdate } = this.getInvalidQuantityGiftItems();
      const unqualifiedItems = this.getUnqualifiedGiftItems();
      
      const hasIssues = orphanedItems.length > 0 || 
                        itemsToRemove.length > 0 || 
                        itemsToUpdate.length > 0 || 
                        unqualifiedItems.length > 0;
      
      if (hasIssues) {
        TierRewardManager.isProcessingGlobally = true;
        TierRewardManager.disableCheckoutButton();
        
        const fixed = await this.fixInvalidGiftItems();
        
        if (fixed) {
          this.rerenderCartContents();
        }
      }
    } catch (error) {
      console.error("Error in initial gift check:", error);
    } finally {
      TierRewardManager.isProcessingGlobally = false;
      TierRewardManager.enableCheckoutButton();
    }
  }

  startOrphanedGiftMonitor() {
    // Clear any existing interval
    if (this.orphanedGiftInterval) {
      clearInterval(this.orphanedGiftInterval);
    }

    this.orphanedGiftInterval = setInterval(async () => {
      if (this.isProcessingCart || TierRewardManager.isProcessingGlobally) {
        return;
      }

      try {
        await this.getCart();
        
        const orphanedItems = this.getOrphanedGiftItems();
        const { itemsToRemove, itemsToUpdate } = this.getInvalidQuantityGiftItems();
        const unqualifiedItems = this.getUnqualifiedGiftItems();
        
        const hasIssues = orphanedItems.length > 0 || 
                          itemsToRemove.length > 0 || 
                          itemsToUpdate.length > 0 || 
                          unqualifiedItems.length > 0;
        
        if (hasIssues) {
          TierRewardManager.isProcessingGlobally = true;
          TierRewardManager.disableCheckoutButton();
          
          const fixed = await this.fixInvalidGiftItems();
          
          if (fixed) {
            this.rerenderCartContents();
            this.updateUI();
          }
        }
      } catch (error) {
        console.error("Error in gift monitor:", error);
      } finally {
        TierRewardManager.isProcessingGlobally = false;
        TierRewardManager.enableCheckoutButton();
      }
    }, 30000);
  }

  async getCart() {
    try {
      const res = await fetch("/cart?view=data");
      const text = await res.text();
      try {
        this.cart = JSON.parse(text);
      } catch (parseError) {
        console.error("Error parsing cart JSON, trying fallback:", parseError);
        const fallbackRes = await fetch("/cart.js");
        this.cart = await fallbackRes.json();
      }

      const sections = this.isCartPage
        ? this.mainCartItems
          .getSectionsToRender()
          .map((section) => section.section)
          .join(",")
        : this.cartEl
          .getSectionsToRender()
          .map((section) => section.id)
          .join(",");
      const cartRes = await fetch(`?sections=${sections}`);
      const sectionJson = await cartRes.json();
      this.sections = sectionJson;

      this.lastCartUpdate = Date.now();
      
      // Update shared cart data
      TierRewardManager.sharedCart = this.cart;
      TierRewardManager.lastGlobalCartUpdate = this.lastCartUpdate;
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  }

  getCartTotal() {
    if (!this.cart || !this.cart.items) {
      return 0;
    }

    const totalWithoutGifts = this.cart.items.reduce((acc, item) => {
      let isGift = false;

      if (item.properties) {
        isGift = isGift || item.properties._gift === "true";
      }

      if (item.product_type) {
        isGift = isGift || item.product_type === "Insurance";
      }

      if (item.tags) {
        if (Array.isArray(item.tags)) {
          isGift = isGift || item.tags.includes("bms-noMotivate");
        } else if (typeof item.tags === "string") {
          isGift =
            isGift ||
            item.tags.split(",").some((t) => t.trim() === "bms-noMotivate");
        }
      }

      return isGift ? acc : acc + item.final_line_price;
    }, 0);

    return totalWithoutGifts;
  }

  getVariantInCart(variantId) {
    if (!this.cart || !this.cart.items) {
      return null;
    }

    return this.cart.items.find(
      (item) =>
        item.variant_id === variantId &&
        item.properties &&
        item.properties._gift === "true"
    );
  }

  getAllItemsWithVariantId(variantId) {
    if (!this.cart || !this.cart.items) {
      return [];
    }

    return this.cart.items.filter(
      (item) =>
        item.variant_id === variantId &&
        item.properties &&
        item.properties._gift === "true"
    );
  }


  getOrphanedGiftItems() {
    if (!this.cart || !this.cart.items) {
      return [];
    }

    const validTierVariantIds = this.tiers.map(tier => tier.variantId);

    return this.cart.items.filter(item => {
      const isGift = item.properties && item.properties._gift === "true";
      const isOrphaned = !validTierVariantIds.includes(item.variant_id);
      return isGift && isOrphaned;
    });
  }

  /**
   * Get all gift items that have quantity > 1 or are duplicates
   * Returns items that need to be fixed
   */
  getInvalidQuantityGiftItems() {
    if (!this.cart || !this.cart.items) {
      return { itemsToRemove: [], itemsToUpdate: [] };
    }

    const validTierVariantIds = this.tiers.map(tier => tier.variantId);
    const giftItemsByVariant = new Map();
    
    this.cart.items.forEach(item => {
      const isGift = item.properties && item.properties._gift === "true";
      if (isGift && validTierVariantIds.includes(item.variant_id)) {
        if (!giftItemsByVariant.has(item.variant_id)) {
          giftItemsByVariant.set(item.variant_id, []);
        }
        giftItemsByVariant.get(item.variant_id).push(item);
      }
    });

    const itemsToRemove = [];
    const itemsToUpdate = [];

    giftItemsByVariant.forEach((items, variantId) => {
      if (items.length > 1) {
        for (let i = 1; i < items.length; i++) {
          itemsToRemove.push(items[i]);
        }
        if (items[0].quantity > 1) {
          itemsToUpdate.push(items[0]);
        }
      } else if (items.length === 1 && items[0].quantity > 1) {
        itemsToUpdate.push(items[0]);
      }
    });

    return { itemsToRemove, itemsToUpdate };
  }

  /**
   * Get gift items that exist but shouldn't (threshold not met)
   */
  getUnqualifiedGiftItems() {
    if (!this.cart || !this.cart.items) {
      return [];
    }

    const total = this.getCartTotal();
    const unqualifiedItems = [];

    for (const tier of this.tiers) {
      const thresholdMet = total >= tier.threshold * this.currencyFactor;
      
      if (!thresholdMet) {
        const items = this.getAllItemsWithVariantId(tier.variantId);
        unqualifiedItems.push(...items);
      }
    }

    return unqualifiedItems;
  }

  /**
   * Fix all invalid gift items (duplicates, wrong quantities, unqualified)
   */
  async fixInvalidGiftItems() {
    const updates = {};
    let hasChanges = false;

    const orphanedItems = this.getOrphanedGiftItems();
    for (const item of orphanedItems) {
      updates[item.key] = 0;
      hasChanges = true;
    }

    const unqualifiedItems = this.getUnqualifiedGiftItems();
    for (const item of unqualifiedItems) {
      updates[item.key] = 0;
      hasChanges = true;
    }

    const { itemsToRemove, itemsToUpdate } = this.getInvalidQuantityGiftItems();
    
    for (const item of itemsToRemove) {
      if (!(item.key in updates)) {
        updates[item.key] = 0;
        hasChanges = true;
      }
    }

    for (const item of itemsToUpdate) {
      if (!(item.key in updates)) {
        updates[item.key] = 1;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      try {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
        await this.getCart();
        return true;
      } catch (error) {
        console.error("Error fixing invalid gift items:", error);
        return false;
      }
    }

    return false;
  }

  /**
   * Remove all orphaned gift items from the cart
   */
  async removeOrphanedGiftItems() {
    const orphanedItems = this.getOrphanedGiftItems();
    
    if (orphanedItems.length === 0) {
      return false;
    }


    try {
      const updates = {};
      
      for (const item of orphanedItems) {
        updates[item.key] = 0;
      }

      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      await this.getCart();
      return true;
    } catch (error) {
      console.error("Error removing orphaned gift items:", error);
      return false;
    }
  }

  async addVariant(variantId) {
    const lockAcquired = await TierRewardManager.acquireLock();
    if (!lockAcquired) {
      console.warn("TierRewardManager: Could not acquire lock for addVariant");
      return;
    }

    try {
      await this.getCart();
      
      const existingItems = this.getAllItemsWithVariantId(variantId);

      if (existingItems.length > 0) {
        const updates = {};
        let needsUpdate = false;

        for (let i = 0; i < existingItems.length; i++) {
          if (i === 0) {
            if (existingItems[i].quantity !== 1) {
              updates[existingItems[i].key] = 1;
              needsUpdate = true;
            }
          } else {
            updates[existingItems[i].key] = 0;
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
          await this.getCart();
        }
      } else {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { _gift: "true" },
          }),
        });
        await this.getCart();
        
        const itemsAfterAdd = this.getAllItemsWithVariantId(variantId);
        if (itemsAfterAdd.length > 1 || (itemsAfterAdd.length === 1 && itemsAfterAdd[0].quantity > 1)) {
          const fixUpdates = {};
          for (let i = 0; i < itemsAfterAdd.length; i++) {
            if (i === 0) {
              fixUpdates[itemsAfterAdd[i].key] = 1;
            } else {
              fixUpdates[itemsAfterAdd[i].key] = 0;
            }
          }
          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: fixUpdates })
          });
          await this.getCart();
        }
      }
    } catch (error) {
      console.error("Error managing variant:", error);
    } finally {
      TierRewardManager.releaseLock();
    }
  }

  async removeVariant(variantId) {
    try {
      const items = this.getAllItemsWithVariantId(variantId);

      for (const item of items) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }

      await this.getCart();
    } catch (error) {
      console.error("Error removing variant:", error);
    }
  }

  async removeVariants(variantIds) {
    try {
      const updates = {};
      for (const variantId of variantIds) {
        const items = this.getAllItemsWithVariantId(variantId);

        for (const item of items) {
          updates[item.key] = 0;
        }
      }

      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      })

      await this.getCart();
    } catch (error) {
      console.error("Error removing variants:", error);
    }
  }

  async updateVariantQuantity(variantId, quantity) {
    const item = this.getVariantInCart(variantId);
    if (!item) return;

    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity }),
      });
    } catch (error) {
      console.error("Error updating variant quantity:", error);
    }
  }

  rerenderCartContents() {
    if (this.isCartPage && this.mainCartItems) {
      this.mainCartItems.renderContents({
        sections: this.sections,
      });
    } else if (this.cartEl) {
      this.cartEl.renderContents({
        sections: this.sections,
      });
    }
  }

  async addMultipleVariants(variantsToAdd) {
    try {
      await this.getCart();
      
      const filteredVariants = variantsToAdd.filter(variant => {
        const existing = this.getAllItemsWithVariantId(variant.id);
        return existing.length === 0;
      });

      if (filteredVariants.length === 0) {
        return; // All variants already exist
      }

      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: filteredVariants }),
      });

      await this.getCart();

      const updates = {};
      let needsFix = false;

      for (const variant of variantsToAdd) {
        const items = this.getAllItemsWithVariantId(variant.id);
        
        if (items.length > 1) {
          for (let i = 1; i < items.length; i++) {
            updates[items[i].key] = 0;
            needsFix = true;
          }
        }
        
        if (items.length > 0 && items[0].quantity !== 1) {
          updates[items[0].key] = 1;
          needsFix = true;
        }
      }

      if (needsFix) {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        await this.getCart();
      }
    } catch (error) {
      console.error("Error managing variant:", error);
    }
  }

  async syncTiersWithCart() {
    const lockAcquired = await TierRewardManager.acquireLock();
    if (!lockAcquired) {
      console.warn("TierRewardManager: Could not acquire lock for syncTiersWithCart");
      return;
    }

    TierRewardManager.isProcessingGlobally = true;
    TierRewardManager.disableCheckoutButton();

    try {
      let cartChanged = await this.fixInvalidGiftItems();

      const total = this.getCartTotal();
      
      const removeUpdates = {};
      for (let tier of this.tiers) {
        const thresholdMet = total >= tier.threshold * this.currencyFactor;

        if (!thresholdMet) {
          const items = this.getAllItemsWithVariantId(tier.variantId);
          for (const item of items) {
            removeUpdates[item.key] = 0;
            cartChanged = true;
          }
        }
      }

      if (Object.keys(removeUpdates).length > 0) {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: removeUpdates })
        });
        await this.getCart();
      }

      const updatedTotal = this.getCartTotal();
      const variantsToAdd = [];
      const quantityUpdates = {};

      for (let tier of this.tiers) {
        const thresholdMet = updatedTotal >= tier.threshold * this.currencyFactor;

        if (thresholdMet) {
          const allItems = this.getAllItemsWithVariantId(tier.variantId);
          
          if (allItems.length === 0) {
            variantsToAdd.push({
              id: tier.variantId,
              quantity: 1,
              properties: { _gift: "true" },
            });
          } else if (allItems.length === 1) {
            if (allItems[0].quantity !== 1) {
              quantityUpdates[allItems[0].key] = 1;
              cartChanged = true;
            }
          } else {
            for (let i = 0; i < allItems.length; i++) {
              if (i === 0) {
                if (allItems[i].quantity !== 1) {
                  quantityUpdates[allItems[i].key] = 1;
                  cartChanged = true;
                }
              } else {
                quantityUpdates[allItems[i].key] = 0;
                cartChanged = true;
              }
            }
          }
        }
      }

      if (Object.keys(quantityUpdates).length > 0) {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: quantityUpdates })
        });
        await this.getCart();
        cartChanged = true;
      }

      if (variantsToAdd.length > 0) {
        await this.addMultipleVariants(variantsToAdd);
        cartChanged = true;
      }

      const finalValidation = await this.validateAndFixGiftItems();
      if (finalValidation) {
        cartChanged = true;
      }

      if (cartChanged) {
        this.rerenderCartContents();
      }

      this.disableGiftItemsInCart();
    } catch (error) {
      console.error("Error syncing tiers with cart:", error);
    } finally {
      TierRewardManager.releaseLock();
      TierRewardManager.isProcessingGlobally = false;
      TierRewardManager.enableCheckoutButton();
    }
  }

  /**
   * Final validation pass to ensure gift items are correct
   * This catches any edge cases that might have been missed
   */
  async validateAndFixGiftItems() {
    const total = this.getCartTotal();
    const updates = {};
    let hasIssues = false;

    for (const tier of this.tiers) {
      const thresholdMet = total >= tier.threshold * this.currencyFactor;
      const allItems = this.getAllItemsWithVariantId(tier.variantId);

      if (!thresholdMet) {
        for (const item of allItems) {
          updates[item.key] = 0;
          hasIssues = true;
        }
      } else {
        if (allItems.length > 1) {
          for (let i = 1; i < allItems.length; i++) {
            updates[allItems[i].key] = 0;
            hasIssues = true;
          }
        }
        if (allItems.length > 0 && allItems[0].quantity !== 1) {
          updates[allItems[0].key] = 1;
          hasIssues = true;
        }
      }
    }

    const orphaned = this.getOrphanedGiftItems();
    for (const item of orphaned) {
      updates[item.key] = 0;
      hasIssues = true;
    }

    if (hasIssues) {
      try {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        await this.getCart();
        return true;
      } catch (error) {
        console.error("Error in final validation:", error);
        return false;
      }
    }

    return false;
  }

  disableGiftItemsInCart() {
    const giftItems = document.querySelectorAll(
      '.cart-item[data-gift="true"], .cart-drawer-item[data-gift="true"]'
    );

    giftItems.forEach((item) => {
      const quantityInput = item.querySelector('input[name="quantity"]');
      const removeButton = item.querySelector("cart-remove-button");

      if (quantityInput) {
        quantityInput.setAttribute("disabled", "disabled");
        quantityInput.style.opacity = "0.6";
      }

      if (removeButton) {
        removeButton.style.display = "none";
      }
    });
  }

  listenToCartChanges() {
    if (TierRewardManager.isCartListenerInitialized) {
      return;
    }

    TierRewardManager.isCartListenerInitialized = true;
    const debounceDelay = 500;
    let globalDebounceTimer = null;
    let globalCartUpdatePending = false;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === "tier-sync") {
        return;
      }

      if (globalCartUpdatePending) {
        clearTimeout(globalDebounceTimer);
      } else {
        globalCartUpdatePending = true;
      }

      clearTimeout(globalDebounceTimer);

      globalDebounceTimer = setTimeout(() => {
        TierRewardManager.allInstances.forEach(instance => {
          if (!instance.isConnected) {
            return;
          }

          const now = Date.now();
          if (now - instance.lastCartUpdate < 500) {
            return;
          }

          instance.syncOnChange().catch((error) => {
            console.error("Error in syncOnChange:", error);
          });
        });

        globalCartUpdatePending = false;
      }, debounceDelay);
    });
  }

  updateUI() {
    this.tiers.forEach((tier, index) => {
      const tierEl = this.querySelector(`.tier-${index + 1}`);
      if (!tierEl) return;

      const thresholdMet =
        this.getCartTotal() >= tier.threshold * this.currencyFactor;
      if (thresholdMet) {
        tierEl.classList.add("unlocked");
      } else {
        tierEl.classList.remove("unlocked");
      }
    });

    const total = this.getCartTotal();
    
    const container = this.querySelector(".tier-sale-progress-bar-container");
    const progressBar = this.progressBar;
    
    if (container && progressBar && this.tiers.length >= 3) {
      const [tier1, tier2, tier3] = this.tiers;

      const iconEls = this.querySelectorAll(".tier-sale-marker-icon");
      const containerRect = container.getBoundingClientRect();
      const iconPositions = Array.from(iconEls).map((el) => {
        const iconRect = el.getBoundingClientRect();
        return (
          ((iconRect.left + iconRect.width / 2 - containerRect.left) /
            containerRect.width) *
          100
        );
      });

      const positions = [0, ...iconPositions, 100];
      const thresholds = [0, tier1.threshold, tier2.threshold, tier3.threshold];

      let percent = 0;
      for (let i = 0; i < thresholds.length - 1; i++) {
        const min = thresholds[i];
        const max = thresholds[i + 1];
        const minPos = positions[i];
        const maxPos = positions[i + 1];

        if (total <= min) {
          percent = minPos;
          break;
        } else if (total > min && total <= max) {
          const progressInRange = (total - min) / (max - min);
          percent = minPos + (maxPos - minPos) * progressInRange;
          break;
        } else {
          percent = maxPos;
        }
      }

      progressBar.style.setProperty("--progress-percent", `${percent}%`);
      progressBar.classList.toggle("empty", percent <= 10);
    }
    
    this.updateProgressText();
  }

  updateProgressText() {
    const total = this.getCartTotal();
    const progressTextEl = this.querySelector(".tier-sale-progress-text");
    if (!progressTextEl) return;

    const formatMoney = (amount) =>
      new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "USD",
      }).format(amount / 100);

    const [tier1, tier2, tier3] = this.tiers;

    let message = "";

    if (total < tier1.threshold) {
      const diff = tier1.threshold - total;
      message = `You're only <span class="highlight-green">${formatMoney(
        diff
      )}</span> away from unlocking your first gift!`;
    } else if (total >= tier1.threshold && total < tier2.threshold) {
      const diff = tier2.threshold - total;
      message = `You've unlocked <span class="highlight-green">${formatMoney(
        tier1.threshold
      )}</span> in Tier 1 gifts so far! Spend <span class="highlight-green">${formatMoney(
        diff
      )}</span> more to unlock Tier 2's gifts`;
    } else if (total >= tier2.threshold && total < tier3.threshold) {
      const diff = tier3.threshold - total;
      message = `You've unlocked <span class="highlight-green">${formatMoney(
        tier2.threshold
      )}</span> in Tier 2 gifts so far! Spend <span class="highlight-green">${formatMoney(
        diff
      )}</span> more to unlock Tier 3's gifts`;
    } else if (total >= tier3.threshold) {
      message = `You've unlocked all tier rewards totaling <span class="highlight-green">${tier3.product_price_money_total}</span> in products!`;
    }

    progressTextEl.innerHTML = message;
  }

  async syncOnChange() {
    if (this.isProcessingCart) {
      return;
    }

    this.isProcessingCart = true;

    try {
      await this.getCart();
      
      if (!this.isUIOnly) {
        await this.syncTiersWithCart();
      }
      
      this.updateUI();
      this.lastCartUpdate = Date.now();

    } catch (error) {
      console.error("Error during syncOnChange:", error);
    } finally {
      this.isProcessingCart = false;
    }
  }
}

customElements.define("tier-progress", TierRewardManager);